import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import loadable from '@loadable/component';
import { useClassName } from '@travpro/hooks';

// import LoadableIcon from 'components/LoadableIcon';

import usePortal from 'hooks/usePortal';

// import './_modal';

// const faWindowClose = loadable.lib(() => import(`@fortawesome/pro-regular-svg-icons/faWindowClose`));

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * A reusable modal component which is rendered inside a Portal and displayed in a fixed card on the page. Does not
 * render any other components or layouts, except `children` and an optional close button.
 *
 * @param {string} id String id for the modal. Used for the portal root id.
 * @param {boolean} show Boolean to show the modal or not.
 * @param {Function} onHide Function to call when the modal is closed.
 * @param {boolean} bgClose Boolean to allow closing the modal by clicking the background.
 * @param {boolean} hideCloseBtn Boolean to hide the default close button.
 * @param {boolean} contentScroll Boolean to allow scrolling in the modal content.
 * @param {Node} children Any content. Rendered in the modal body.
 * @returns {JSX.Element} A modal component.
 */
const Modal = ({
  id = 'modal__root',
  show,
  onHide,
  bgClose = false,
  hideCloseBtn = false,
  contentScroll = false,
  theme = "branded",
  children = null,
}) => {
  const c = useClassName('modal');

  const Portal = usePortal(id || 'modal__root');

  // Wraps the onHide func param (if any) to trigger after closing the modal state.
  const closeModal = () => {
    if (onHide) onHide();
  };

  // adds a class to the body to disable scrolling when the modal is open.
  // Todo: investigate alternative that does not require manually manipulating the DOM, without requiring each view to check for a modal manually.
  useEffect(() => {
    if (show) {
      document.body.classList.add('modal__parent--scroll-disabled');
    } else {
      document.body.classList.remove('modal__parent--scroll-disabled');
    }

    return () => {
      document.body.classList.remove('modal__parent--scroll-disabled');
    };
  }, [show]);

  return (
    <Portal rootId={id} prepend>
      {show && (
        <div {...c()}>
          <div
            {...c('overlay', 'scroll-disabled')}
            onClick={bgClose ? closeModal : () => {}}
          />
          <div {...c('content', theme, { 'scroll-enabled': contentScroll })}>
            <div {...c('grid')}>
              {!hideCloseBtn && (
                <div {...c('row', 'justify-end')}>
                  <div {...c('header')}>
                    <button {...c('close', theme)} type="button" onClick={closeModal}>
                      <FontAwesomeIcon icon={['fas', 'window-close']} />
                    </button>
                  </div>
                </div>
              )}
              <div {...c('row')}>
                <div {...c('body')}>{children}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Portal>
  );
};

Modal.propTypes = {
  id: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  bgClose: PropTypes.bool,
  hideCloseBtn: PropTypes.bool,
  contentScroll: PropTypes.bool,
  theme: PropTypes.oneOf(["light", "dark"]), // currently only affects modal background + close button color
  children: PropTypes.node,
};

export default Modal;
